import { RefObject, useEffect, useRef } from 'react';

type UseInfiniteScrollProps = {
  onLoadMore: () => void | Promise<void>;
  loading?: boolean;
  hasNextPage?: boolean;
  rootRef?: RefObject<HTMLElement | null>;
  /** Pixels before the sentinel at which to fire. Default 0 (exact intersection). */
  rootMargin?: string;
};

/**
 * Attach the returned ref to a sentinel element at the bottom of a scrollable
 * list. When the sentinel enters the viewport of `rootRef` (or the browser
 * viewport if omitted), `onLoadMore` fires.
 *
 * After `onLoadMore` resolves, the sentinel is unobserved and re-observed.
 * This is important for data sources where `loading` does not toggle on paginated
 * fetches (e.g., Apollo's `fetchMore` keeps `loading=false` throughout): the new
 * items render, but the sentinel may still be in view because they all fit. A
 * plain IntersectionObserver only fires on state transitions — re-observing
 * forces a fresh initial-intersection check and lets the fetch loop continue
 * until the sentinel scrolls off-screen or `hasNextPage` is `false`.
 *
 * `onLoadMore` is stored in a ref so passing a new closure each render does not
 * tear down the observer.
 */
export const useInfiniteScroll = ({
  onLoadMore,
  loading,
  hasNextPage = true,
  rootRef,
  rootMargin,
}: UseInfiniteScrollProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    let firing = false;
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loading) return;
        if (hasNextPage === false) return;
        if (firing) return;
        firing = true;
        try {
          await onLoadMoreRef.current();
        } finally {
          firing = false;
          const { current } = sentinelRef;
          if (current) {
            observer.unobserve(current);
            observer.observe(current);
          }
        }
      },
      { root: rootRef?.current, rootMargin, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, hasNextPage, rootRef, rootMargin]);

  return sentinelRef;
};
